import { useContext, useMemo } from "react";
import { Dayjs } from "dayjs";
import {
  BreadTransaction,
  CategoryType,
  FirestoreDocCategory,
  Merchant,
} from "breadcommon";
import { TransactionsContext } from "../firebaseio/TransactionsContext";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import { PreferencesContext } from "../firebaseio/PreferencesContext";
import { useExchangeRates } from "../firebaseio/ExchangeRatesContext";

export const MERCHANT_TYPES = ["expenses", "income"] as const;
export type MerchantType = (typeof MERCHANT_TYPES)[number];

/** What the merchants are ranked by, and what their bars show. */
export type MerchantSort = "total" | "frequency";

const CATEGORY_TYPE_BY_MERCHANT_TYPE: Record<MerchantType, CategoryType> = {
  expenses: CategoryType.Expense,
  income: CategoryType.Income,
};

export interface MerchantTotal {
  merchant: Merchant;
  /** The net amount, positive in the type's usual direction. */
  total: number;
  /** Every transaction counted, refunds included. */
  count: number;
  transactions: BreadTransaction[];
  firstDate: Dayjs;
  lastDate: Dayjs;
}

export interface TopMerchantsData {
  /** Ranked, first to last. */
  merchants: MerchantTotal[];
  currency: string;
}

export function useTopMerchantsData(
  beginDate: Dayjs,
  endDate: Dayjs,
  type: MerchantType,
  sort: MerchantSort,
): TopMerchantsData {
  const transactions = useContext(TransactionsContext);
  const categories = useContext(CategoriesContext);
  const preferences = useContext(PreferencesContext);
  const { convertTransactionsToSpecificCurrency } = useExchangeRates();

  const currency = preferences?.web?.default_currency ?? "USD";

  return useMemo(
    () =>
      buildTopMerchantsData({
        transactions: convertTransactionsToSpecificCurrency(
          Array.from(transactions.values()),
          currency,
        ),
        categories,
        beginDate,
        endDate,
        type,
        sort,
        currency,
      }),
    [
      transactions,
      categories,
      beginDate,
      endDate,
      type,
      sort,
      currency,
      convertTransactionsToSpecificCurrency,
    ],
  );
}

/**
 * Totals each merchant's reviewed transactions over the range, in categories
 * of one type, and ranks them. Transactions are expected to have already been
 * converted into `currency`.
 *
 * A merchant's total is the plain signed sum of its transactions, so refunds
 * net against purchases; expenses are negated to make spending positive.
 * Merchants are told apart by name. A merchant that nets to zero or less
 * can't be drawn, so it's left out.
 *
 * Transactions without a merchant are left out, as are unreviewed
 * transactions, whose category is only tentative.
 */
export function buildTopMerchantsData({
  transactions,
  categories,
  beginDate,
  endDate,
  type,
  sort,
  currency,
}: {
  transactions: BreadTransaction[];
  categories: Map<string, FirestoreDocCategory>;
  beginDate: Dayjs;
  endDate: Dayjs;
  type: MerchantType;
  sort: MerchantSort;
  currency: string;
}): TopMerchantsData {
  const categoryType = CATEGORY_TYPE_BY_MERCHANT_TYPE[type];
  const sign = type === "expenses" ? -1 : 1;

  const byName = new Map<string, MerchantTotal>();

  transactions.forEach((transaction) => {
    if (transaction.date < beginDate || transaction.date > endDate) return;
    if (!transaction.reviewed) return;
    if (!transaction.categoryId) return;
    if (categories.get(transaction.categoryId)?.type !== categoryType) return;

    if (transaction.merchant === null) return;

    const amount = sign * transaction.amount;

    const existing = byName.get(transaction.merchant.name);
    if (!existing) {
      byName.set(transaction.merchant.name, {
        merchant: transaction.merchant,
        total: amount,
        count: 1,
        transactions: [transaction],
        firstDate: transaction.date,
        lastDate: transaction.date,
      });
      return;
    }
    existing.total += amount;
    existing.count++;
    existing.transactions.push(transaction);
    if (transaction.date < existing.firstDate) {
      existing.firstDate = transaction.date;
    }
    if (transaction.date > existing.lastDate) {
      existing.lastDate = transaction.date;
    }
    // Some transactions come without the merchant's logo.
    if (existing.merchant.logo === null && transaction.merchant.logo !== null) {
      existing.merchant = transaction.merchant;
    }
  });

  const merchants = Array.from(byName.values())
    .filter((merchant) => merchant.total > 0)
    .sort(
      (a, b) =>
        (sort === "total"
          ? b.total - a.total || b.count - a.count
          : b.count - a.count || b.total - a.total) ||
        a.merchant.name.localeCompare(b.merchant.name),
    );

  return { merchants, currency };
}
